import { Host, forceUpdate, h } from "@stencil/core";
import { tokenMap, updateDirectionInImageCustomVar } from "../../common/utils";
import { ACCORDION_PARTS_DICTIONARY, DISABLED_CLASS } from "../../common/reserved-names";
import { getControlRegisterProperty } from "../../common/registry-properties";
let GET_IMAGE_PATH_CALLBACK_REGISTRY;
const DEFAULT_GET_IMAGE_PATH_CALLBACK = imageSrc => ({ base: imageSrc });
const ELEMENTS_TO_PREVENT_EXPAND_COLLAPSE = ["input", "textarea"];
/**
 * @status experimental
 */
export class ChAccordionRender {
    constructor() {
        this.disabled = false;
        this.expandableButtonPosition = "end";
        this.getImagePathCallback = undefined;
        this.model = undefined;
        this.singleItemExpanded = false;
    }
    #images = new Map();
    /**
     * Useful to track all expand/collapse interactions in order to close all
     * items expect for the last expanded when switching to `singleItemExpanded`
     */
    // eslint-disable-next-line @stencil-community/own-props-must-be-private
    #expandedItems = new Set();
    /**
     * Useful to rendering items after the first expansion.
     */
    // eslint-disable-next-line @stencil-community/own-props-must-be-private
    #renderedItems = new Set();
    el;
    getImagePathCallbackChanged() {
        this.#computeImages();
    }
    modelChanged() {
        this.#computeImages();
        this.#expandedItems.clear();
        this.#renderedItems.clear();
        this.model?.forEach(item => {
            if (item.expanded) {
                this.#expandedItems.add(item.id);
                this.#renderedItems.add(item.id);
            }
        });
        this.#closeAllExpandedItemsExceptForTheLast(false);
    }
    singleItemExpandedChanged() {
        this.#closeAllExpandedItemsExceptForTheLast(true);
    }
    /**
     * Fired when an item is expanded or collapsed
     */
    expandedChange;
    #computeImage = (imageSrc) => {
        if (!imageSrc) {
            return undefined;
        }
        const getImagePathCallback = this.getImagePathCallback ?? GET_IMAGE_PATH_CALLBACK_REGISTRY;
        if (!getImagePathCallback) {
            return undefined;
        }
        const img = getImagePathCallback(imageSrc);
        return img
            ? updateDirectionInImageCustomVar(img, "start")
            : undefined;
    };
    #computeImages = () => {
        this.#images.clear();
        this.model?.forEach(itemUIModel => {
            const itemImage = this.#computeImage(itemUIModel.startImgSrc);
            if (itemImage) {
                this.#images.set(itemUIModel.id, itemImage);
            }
        });
    };
    #handleHeaderToggle = (event) => {
        const composedPath = event.composedPath();
        const headerRef = composedPath.find(el => el.tagName?.toLowerCase() === "button");
        if (!headerRef ||
            headerRef.getRootNode() !== this.el.shadowRoot ||
            ELEMENTS_TO_PREVENT_EXPAND_COLLAPSE.includes(composedPath[0].tagName?.toLowerCase())) {
            return;
        }
        const itemId = headerRef.id;
        const itemUIModel = this.model.find(item => item.id === itemId);
        if (itemUIModel.disabled) {
            return;
        }
        const newExpandedValue = !itemUIModel.expanded;
        this.#updateExpandedOnItem(itemUIModel, newExpandedValue);
    };
    #updateExpandedOnItem = (itemUIModel, newExpandedValue) => {
        // Collapse all opened items and emit expandedChange
        if (this.singleItemExpanded && this.#expandedItems.size > 0) {
            this.model.forEach(itemUIModelToCollapse => {
                if (itemUIModelToCollapse.expanded &&
                    itemUIModelToCollapse.id !== itemUIModel.id) {
                    itemUIModelToCollapse.expanded = false;
                    this.expandedChange.emit({
                        id: itemUIModelToCollapse.id,
                        expanded: false
                    });
                }
            });
            this.#expandedItems.clear();
        }
        // If the item is expanded, added it to the Set
        if (newExpandedValue) {
            this.#expandedItems.add(itemUIModel.id);
            this.#renderedItems.add(itemUIModel.id);
        }
        // Otherwise, remove it
        else {
            this.#expandedItems.delete(itemUIModel.id);
        }
        itemUIModel.expanded = newExpandedValue;
        this.expandedChange.emit({
            id: itemUIModel.id,
            expanded: newExpandedValue
        });
        forceUpdate(this);
    };
    #renderItem = (item, index) => {
        const startImage = this.#images.get(item.id);
        const startImageClasses = startImage?.classes;
        const isDisabled = item.disabled ?? this.disabled;
        return (h("div", { class: { panel: true, "panel--expanded": item.expanded }, key: item.id, part: tokenMap({
                [item.id]: true,
                [ACCORDION_PARTS_DICTIONARY.PANEL]: true,
                [ACCORDION_PARTS_DICTIONARY.DISABLED]: isDisabled,
                [ACCORDION_PARTS_DICTIONARY.EXPANDED]: item.expanded,
                [ACCORDION_PARTS_DICTIONARY.COLLAPSED]: !item.expanded
            }) }, h("button", { id: item.id, "aria-controls": `section-${index}`, "aria-label": item.accessibleName || undefined, "aria-expanded": item.expanded ? "true" : "false", class: {
                header: true,
                [`header--expand-button-${this.expandableButtonPosition}`]: true,
                [`header--expand-button-${this.expandableButtonPosition}-collapsed`]: !item.expanded,
                [`header--expand-button-${this.expandableButtonPosition}-expanded`]: item.expanded,
                [DISABLED_CLASS]: isDisabled,
                [`start-img-type--${item.startImgType ?? "background"} pseudo-img--start`]: !!startImage,
                [startImageClasses]: !!startImageClasses
            }, part: tokenMap({
                [item.id]: true,
                [item.headerSlotId]: !!item.headerSlotId,
                [ACCORDION_PARTS_DICTIONARY.HEADER]: true,
                [ACCORDION_PARTS_DICTIONARY.DISABLED]: isDisabled,
                [ACCORDION_PARTS_DICTIONARY.EXPANDED]: item.expanded,
                [ACCORDION_PARTS_DICTIONARY.COLLAPSED]: !item.expanded
            }), style: startImage?.styles ?? undefined, disabled: isDisabled, type: "button" }, item.headerSlotId ? h("slot", { name: item.headerSlotId }) : item.caption), h("section", { id: `section-${index}`, "aria-label": item.accessibleName || undefined, "aria-labelledby": !item.accessibleName ? item.id : undefined, class: !item.expanded ? "section--hidden" : undefined }, (this.#renderedItems.has(item.id) || !item.id) && (h("div", { class: "sub-section", part: tokenMap({
                [item.id]: true,
                [ACCORDION_PARTS_DICTIONARY.SECTION]: true,
                [ACCORDION_PARTS_DICTIONARY.DISABLED]: isDisabled,
                [ACCORDION_PARTS_DICTIONARY.EXPANDED]: item.expanded,
                [ACCORDION_PARTS_DICTIONARY.COLLAPSED]: !item.expanded
            }) }, h("slot", { name: item.id }))))));
    };
    #closeAllExpandedItemsExceptForTheLast = (emitExpandedChangeEvent) => {
        if (!this.singleItemExpanded || this.#expandedItems.size <= 1) {
            return;
        }
        const lastItemId = [...this.#expandedItems.keys()].at(-1);
        // Close all items except for the last and emit the expandedChange event
        this.model.forEach(itemUIModel => {
            if (itemUIModel.expanded && itemUIModel.id !== lastItemId) {
                itemUIModel.expanded = false;
                if (emitExpandedChangeEvent) {
                    this.expandedChange.emit({ id: itemUIModel.id, expanded: false });
                }
            }
        });
        this.#expandedItems.clear();
        this.#expandedItems.add(lastItemId);
    };
    #computeGridTemplateRows = () => this.model
        .map(item => item.expanded
        ? item.expandedSize ?? "max-content"
        : this.#getCollapsedSizeForUnit(item.expandedSize))
        .join(" ");
    #getCollapsedSizeForUnit = (expandedSize) => expandedSize && expandedSize.includes("fr") ? "0fr" : "max-content";
    connectedCallback() {
        // Initialize default getImagePathCallback
        GET_IMAGE_PATH_CALLBACK_REGISTRY ??=
            getControlRegisterProperty("getImagePathCallback", "ch-accordion-render") ?? DEFAULT_GET_IMAGE_PATH_CALLBACK;
        this.#computeImages();
        this.model?.forEach(item => {
            if (item.expanded) {
                this.#expandedItems.add(item.id);
                this.#renderedItems.add(item.id);
            }
        });
        this.#closeAllExpandedItemsExceptForTheLast(false);
    }
    render() {
        return (h(Host
        // TODO: Add support to prevent expand/collapse when pressing the space
        // key on an input/textarea
        , { key: '670680d731c526ecef7ea0d96d7fcc9c07d8ffa1',
            // TODO: Add support to prevent expand/collapse when pressing the space
            // key on an input/textarea
            style: this.model != null
                ? {
                    "--ch-accordion-grid-template-rows": this.#computeGridTemplateRows()
                }
                : undefined, onClick: this.#handleHeaderToggle }, (this.model ?? []).map(this.#renderItem)));
    }
    static get is() { return "ch-accordion-render"; }
    static get encapsulation() { return "shadow"; }
    static get originalStyleUrls() {
        return {
            "$": ["accordion.scss"]
        };
    }
    static get styleUrls() {
        return {
            "$": ["accordion.css"]
        };
    }
    static get properties() {
        return {
            "disabled": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "This attribute lets you specify if all accordions are disabled.\nIf disabled,accordions will not fire any user interaction related event\n(for example, `expandedChange` event)."
                },
                "attribute": "disabled",
                "reflect": false,
                "defaultValue": "false"
            },
            "expandableButtonPosition": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "\"start\" | \"end\"",
                    "resolved": "\"end\" | \"start\"",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Specifies the position of the expandable button in the header of the\npanels."
                },
                "attribute": "expandable-button-position",
                "reflect": false,
                "defaultValue": "\"end\""
            },
            "getImagePathCallback": {
                "type": "unknown",
                "mutable": false,
                "complexType": {
                    "original": "(\n    imageSrc: string\n  ) => GxImageMultiState | undefined",
                    "resolved": "(imageSrc: string) => GxImageMultiState",
                    "references": {
                        "GxImageMultiState": {
                            "location": "import",
                            "path": "../../common/types",
                            "id": "src/common/types.ts::GxImageMultiState"
                        }
                    }
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "This property specifies a callback that is executed when the path for an\nstartImgSrc needs to be resolved."
                }
            },
            "model": {
                "type": "unknown",
                "mutable": false,
                "complexType": {
                    "original": "AccordionModel | undefined",
                    "resolved": "AccordionItemModel[]",
                    "references": {
                        "AccordionModel": {
                            "location": "import",
                            "path": "./types",
                            "id": "src/components/accordion/types.ts::AccordionModel"
                        }
                    }
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "Specifies the items of the control."
                }
            },
            "singleItemExpanded": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "If `true` only one item will be expanded at the same time."
                },
                "attribute": "single-item-expanded",
                "reflect": false,
                "defaultValue": "false"
            }
        };
    }
    static get events() {
        return [{
                "method": "expandedChange",
                "name": "expandedChange",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "Fired when an item is expanded or collapsed"
                },
                "complexType": {
                    "original": "AccordionItemExpandedChangeEvent",
                    "resolved": "{ id: string; expanded: boolean; }",
                    "references": {
                        "AccordionItemExpandedChangeEvent": {
                            "location": "import",
                            "path": "./types",
                            "id": "src/components/accordion/types.ts::AccordionItemExpandedChangeEvent"
                        }
                    }
                }
            }];
    }
    static get elementRef() { return "el"; }
    static get watchers() {
        return [{
                "propName": "getImagePathCallback",
                "methodName": "getImagePathCallbackChanged"
            }, {
                "propName": "model",
                "methodName": "modelChanged"
            }, {
                "propName": "singleItemExpanded",
                "methodName": "singleItemExpandedChanged"
            }];
    }
}
